/**
 * Design Tokens - Shared CSS custom properties.
 *
 * Import and spread into component styles:
 * ```
 * static override styles = [tokens, css`...`];
 * ```
 *
 * Promotion rule: add a token when a value is reused by 2+ components, or
 * when it's semantically meaningful enough to deserve a name (a backdrop
 * color, a button-press shadow). Single-use values stay inline. Two names
 * for one value are fine when they mean different things: a backdrop and a
 * chip are both half-black, and may stop being so separately. A token
 * nothing reads goes.
 */

import { css } from 'lit';

export const tokens = css`
  :host {
    /* Background Colors */
    --color-bg-panel: rgba(0, 0, 0, 0.9);
    --color-bg-modal: rgba(50, 50, 50, 0.95);
    --color-bg-backdrop: rgba(0, 0, 0, 0.5);
    --color-bg-chip: rgba(0, 0, 0, 0.5);
    /* Bottom sheets (mobile chat drawer) — a step less translucent than the
       chips; sheets carry reading text over a live game. */
    --color-bg-sheet: rgba(0, 0, 0, 0.6);
    --color-bg-input: #1e1e1e;
    --color-bg-hover: #282828;
    --color-bg-selected: #3c3c3c;
    --color-bg-item-hover: #505050;
    --color-bg-tooltip: #1a1a1a;

    /* Text Colors */
    --color-text-primary: #d2d2d2;
    --color-text-bright: #e6e6e6;
    --color-text-heading: #dedebf; /* warm cream for prominent notice headings — level-up, match results, notifications */
    --color-text-secondary: #787878;
    --color-text-tertiary: #8c8c8c;
    --color-text-muted: #a0a0a0;
    --color-text-tooltip: #c8c8c8;
    --color-text-link: #dcdcdc;
    --color-link-url: #bfbfff; /* autolinked URLs in chat / debug text (distinct from the grey UI-nav --color-text-link) */
    --color-text-white: #ffffff;
    --color-text-error: #ff6b6b;

    /* Border Colors */
    --color-border-dark: #141414;
    --color-border-light: rgba(255, 255, 255, 0.08);
    --color-border-subtle: rgba(255, 255, 255, 0.06);
    --color-border-strong: rgba(0, 0, 0, 0.3);
    --color-border-soft: rgba(0, 0, 0, 0.2);

    /* Button Colors */
    --color-btn-play: #228a12;
    --color-btn-play-hover: #399c29;
    --color-btn-login: #447da7;
    --color-btn-login-hover: #6494b7;
    --color-btn-disabled: #555555;

    /* Game/Team Colors */
    --color-team-blue: #4d7fd5;
    --color-team-red: #dc4f46;
    --color-players-active: #ffec52;
    --color-cruise-active: #39ff14; /* neon green — cruise-mode-on icon (mask-recoloured) */
    --color-accent-gold: #ffcc00; /* gold highlight for notable events — CTF capture, BTR win, bounty reward */

    /* Chat message identity (game-chat, mobile-chat, upcoming chat peek):
       tag = the TEAM/TO/FROM chip background, text = the message tint */
    --color-chat-team: #6d57a0;
    --color-chat-team-text: #c6b9e4;
    --color-chat-whisper: #47864c;
    --color-chat-whisper-text: #78d07f;

    /* Score-change feedback (kill/death notices) */
    --color-score-positive: #29bd11;
    --color-score-negative: #ce3228;

    /* Medal Gradients */
    --gradient-medal-gold: linear-gradient(135deg, #ffd700 0%, #ffb800 100%);
    --gradient-medal-silver: linear-gradient(135deg, #c0c0c0 0%, #a0a0a0 100%);
    --gradient-medal-bronze: linear-gradient(135deg, #cd7f32 0%, #a06020 100%);

    /* Selectors (aircraft/game picker chips) */
    --color-bg-selector: rgba(0, 0, 0, 0.3);
    --color-bg-selector-hover: rgba(0, 0, 0, 0.8);
    --color-bg-selector-lighted: rgba(108, 195, 108, 0.6);
    --color-bg-selector-lighted-hover: rgba(80, 160, 80, 0.6);

    /* Stat Bar */
    --color-bg-stat-bar: rgba(60, 60, 60, 0.7);
    --gradient-stat-bar: linear-gradient(to bottom, #4bb8e8 0%, #3b9acd 50%, #2d7aa3 100%);

    /* Badge Colors */
    --color-bg-badge: #505050;

    /* Sliders (range-slider, zoom-slider) */
    --color-slider-track: var(--color-overlay-medium);
    --color-slider-thumb: #ffffff;

    /* White overlays on dark surfaces — the most-reused white-alpha values,
       for faint hover backgrounds, hairline borders, slider tracks and
       scrollbar thumbs. */
    --color-overlay-subtle: rgba(255, 255, 255, 0.1);
    /* The context menu's border and hover row, the drawer's page marker */
    --color-overlay-light: rgba(255, 255, 255, 0.15);
    --color-overlay-medium: rgba(255, 255, 255, 0.2);
    --color-overlay-strong: rgba(255, 255, 255, 0.3);

    /* Ghost buttons (subtle white-on-dark, e.g. slider reset) */
    --color-bg-btn-ghost: var(--color-overlay-subtle);
    --color-bg-btn-ghost-hover: var(--color-overlay-medium);
    --color-bg-btn-ghost-active: var(--color-overlay-light);

    /* Typography */
    --font-family: 'MontserratWeb', Helvetica, sans-serif;
    --font-size-xxs: 10px;
    --font-size-xs: 11px;
    --font-size-sm: 12px;
    --font-size-13: 13px; /* between sm/md — the most-used HUD/body text size; numeric name as the t-shirt ramp has no slot here */
    --font-size-md: 14px;
    --font-size-lg: 18px;
    --font-size-xl: 20px;
    --font-size-xxl: 22px;
    --font-size-16: 16px; /* between md/lg, the ramp has no slot: panel body text, the bigger buttons, the notification text */

    /* Border Radii. xs is the small inset things: keycaps, badges, the chips
       inside a line of chat, a slider's track. chip is the name chip of the
       kill notice and the match results (legacy's 7px); the death notice's
       bigger chip is lg, as legacy had it. */
    --radius-xs: 3px;
    --radius-sm: 4px;
    --radius-md: 5px;
    --radius-chip: 7px;
    --radius-lg: 9px;

    /* Shadows */
    --shadow-panel: 0 10px 59px -9px rgba(0, 0, 0, 0.7);
    --shadow-input: 0 0 16px 0 rgba(0, 0, 0, 0.6);
    --shadow-btn-hover: 0 0 10px 0 #000;
    --shadow-btn-hover-soft: 0 0 10px 0 rgba(0, 0, 0, 0.3);
    --shadow-text: 0 0 3px rgba(0, 0, 0, 0.8);
    --shadow-text-btn: 0 -1px 0 rgba(0, 0, 0, 0.6);
    /* Small raised control lifted off its track or backing — slider thumbs,
       the toggle knob, the selected segment. */
    --shadow-control: 0 2px 4px rgba(0, 0, 0, 0.3);
    --shadow-notice: 0 0 10px rgba(0, 0, 0, 0.5);
    --color-shadow-lighted: rgba(108, 195, 108, 0.4);
    /* Green glow around a selector that can be acted on right now — an
       upgrade you have a point to spend on (sidebar and mobile loadout). */
    --shadow-lighted: 0 0 30px 0 var(--color-shadow-lighted);
  }
`;
